import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import {
  Box,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EnhancedTable from '../../../Shared/EnhancedTable/EnhancedTable';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import {
  openDicomViewer,
  markDicomAsViewed,
} from '../../../../Services/DicomService';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import styles from './RecordDicomsDialog.style';
import { getPartDicomCdsDownloadUrls } from '../../../../Services/DicomService';
import { downloadFile } from '../../../../Services/FileService';
import { tableStyles } from '../../CaseDashboard.style';

const combinedStyles = (theme) => ({
  ...tableStyles(theme),
  ...styles(theme),
});

const headCells = [
  {
    id: 'studyDescription',
    label: 'Study Description',
    width: '8%',
    default: 'No Description',
  },
  {
    id: 'studyDate',
    label: 'Study Date',
    width: '8%',
    isDate: true,
    dateOptions: { isUTC: true, convertUTCToLocalTimeZone: false },
  },
  { id: 'sharedBy', label: 'Shared By', width: '8%' },
  { id: 'sharedAt', label: 'Share Date', width: '12%', isDate: true },
  {
    id: 'lastReviewedAt',
    label: 'Last Reviewed',
    width: '8%',
    isDate: true,
  },
  {
    id: 'expiresAt',
    label: 'Expiration Date',
    width: '8%',
    isDate: true,
  },
  { id: 'actions', label: 'Actions', width: '12%' },
];

function GroupDownloadButton({
  groupKey,
  groupRows,
  orderId,
  isDownloadAllowed,
  classes,
  showNotification,
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadVariant, setDownloadVariant] = useState('indeterminate');
  const [individualProgress, setIndividualProgress] = useState({});

  const canGroupBeDownloaded =
    groupRows && groupRows.every((row) => row.canBeDownloaded);
  const eligibleUploadedDate =
    groupRows && groupRows.every((row) => row.isEligibleUploadedDate);

  // Calculate overall progress from individual downloads
  React.useEffect(() => {
    const progressValues = Object.values(individualProgress);
    if (progressValues.length === 0) return;

    // Check if any download is in determinate mode
    const determinateDownloads = progressValues.filter(
      (p) => p.variant === 'determinate'
    );

    if (determinateDownloads.length > 0) {
      // Calculate average progress of all downloads
      const totalProgress = progressValues.reduce(
        (sum, p) => sum + p.progress,
        0
      );
      const averageProgress = totalProgress / progressValues.length;

      setDownloadVariant('determinate');
      setDownloadProgress(averageProgress);
    } else {
      // All downloads are indeterminate
      setDownloadVariant('indeterminate');
      setDownloadProgress(0);
    }
  }, [individualProgress]);

  const handleDownloadGroup = async () => {
    if (!canGroupBeDownloaded || !isDownloadAllowed || isDownloading) {
      return;
    }

    setIsDownloading(true);
    setDownloadVariant('indeterminate');
    setDownloadProgress(0);

    try {
      const { dicomCdUris, errorMessage } = await getPartDicomCdsDownloadUrls(
        orderId,
        groupKey
      );

      if (!!errorMessage || !dicomCdUris || !dicomCdUris.length) {
        showNotification &&
          showNotification(
            `There is an issue with downloading the DICOMs for this order. Please contact your Client Manager for assistance.`,
            'error'
          );

        setIsDownloading(false);
        setDownloadVariant('indeterminate');
        setDownloadProgress(0);

        return;
      }

      // Initialize individual progress tracking
      const initialProgress = {};
      dicomCdUris.forEach((_, index) => {
        initialProgress[index] = { variant: 'indeterminate', progress: 0 };
      });
      setIndividualProgress(initialProgress);

      // Start downloads with 1.5 second gaps between initiations and wait for all to complete
      const downloadPromises = dicomCdUris.map((dicomCdUri, index) => {
        const { cdName, uri } = dicomCdUri;

        return new Promise((resolve, reject) => {
          setTimeout(async () => {
            try {
              // Create individual progress handlers for this download
              const setIndividualVariant = (variant) => {
                setIndividualProgress((prev) => ({
                  ...prev,
                  [index]: { ...prev[index], variant },
                }));
              };

              const setIndividualProgressValue = (progress) => {
                setIndividualProgress((prev) => ({
                  ...prev,
                  [index]: {
                    ...prev[index],
                    progress: Math.max(prev[index].progress, progress),
                  },
                }));
              };

              await downloadFile(
                uri,
                cdName,
                false, // inProgress
                null, // setInprogress (we handle this at group level)
                setIndividualVariant,
                setIndividualProgressValue
              );
              resolve();
            } catch (error) {
              reject(error);
            }
          }, index * 1500); // 1.5 seconds (1500ms) gap between each download start
        });
      });

      // Wait for all downloads to complete
      await Promise.all(downloadPromises);

      setIsDownloading(false);
      setDownloadVariant('indeterminate');
      setDownloadProgress(0);

      showNotification &&
        showNotification(
          `Part ${groupKey} DICOMs downloaded successfully`,
          'success'
        );
    } catch (error) {
      console.error('Error downloading DICOM group:', error);
      showNotification &&
        showNotification(
          `An error occurred while downloading the DICOM CD Please try again later.`,
          'error'
        );

      setIsDownloading(false);
      setDownloadVariant('indeterminate');
      setDownloadProgress(0);
    }
  };

  const isDownloadDisabled =
    !canGroupBeDownloaded || !isDownloadAllowed || isDownloading;

  const getTooltipMessage = () => {
    if (isDownloading) {
      return 'Download in progress...';
    }
    if (!eligibleUploadedDate && groupKey) {
      return `Part ${groupKey} contains DICOM studies produced before 11/19/2024 that are not eligible for download`;
    }
    if (!isDownloadAllowed || !canGroupBeDownloaded) {
      return 'Download disabled on these studies';
    }
    return '';
  };

  const downloadButton = (
    <Button
      variant="contained"
      size="small"
      className={classes.viewBtn}
      onClick={handleDownloadGroup}
      disabled={isDownloadDisabled}
      data-testid={`dicom-group-download-${groupKey}`}
    >
      {isDownloading ? (
        <CircularProgress
          color="inherit"
          size={20}
          thickness={4}
          variant={downloadVariant}
          value={downloadProgress}
        />
      ) : (
        `Download (${groupRows.length} ${
          groupRows.length === 1 ? 'Study' : 'Studies'
        })`
      )}
    </Button>
  );

  return (
    <div className={classes.groupDownloadCenterContainer}>
      {isDownloadDisabled ? (
        <Tooltip title={getTooltipMessage()} arrow>
          <span>{downloadButton}</span>
        </Tooltip>
      ) : (
        downloadButton
      )}
    </div>
  );
}

// action buttons
function ViewButton(props) {
  const { classes, row, setRow, orderId } = props;

  const onViewButtonClick = async () => {
    const orgLastReviewedValue = row['lastReviewedAt'];

    try {
      const studyId = row['studyId'];
      const dicomViewerWindow = openDicomViewer(studyId);

      if (!dicomViewerWindow.isOpen) {
        console.error(`Failed to open ${dicomViewerWindow.windowName}`);
        return;
      }

      await markDicomAsViewed(orderId, row.id);

      // Update UI
      row['lastReviewedAt'] = moment();
      setRow(row);
    } catch (err) {
      row['lastReviewedAt'] = orgLastReviewedValue;
      setRow(row);
      console.error(err);
    }
  };

  return (
    <div className={classes.viewButtonContainer}>
      <Button
        data-testid="caseRecordViewButton"
        variant="contained"
        size="small"
        className={classes.viewBtn}
        onClick={onViewButtonClick}
      >
        View
      </Button>
    </div>
  );
}

const RecordDicomsDialog = (props) => {
  const {
    classes,
    open,
    setOpen,
    record,
    orderId,
    showNotification,
    isDownloadAllowed,
    showDicomCdDownload,
  } = props;

  const customCells = {
    actions: <ViewButton classes={classes} orderId={orderId} />,
  };

  const closeDialogHandler = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      id="record-dicoms-dialog"
      onClose={closeDialogHandler}
      aria-labelledby="record-dicoms-dialog-title"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
    >
      <DialogTitle
        id="record-dicoms-dialog-title"
        className={classes.dialogHeader}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          SHARED DICOM STUDIES
          <IconButton
            aria-label="close"
            data-testid="close-button"
            onClick={closeDialogHandler}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.dialogBody}>
        <Box style={{ marginBottom: '10px' }}>
          <Box style={{ marginBottom: '10px' }} className={classes.odd}>
            <Typography style={{ padding: 10, fontSize: '16px' }}>
              <Typography component="span" className={classes.headerTitle}>
                Location Name:
              </Typography>
              {record.locationName}
            </Typography>
          </Box>
          <Box display={'flex'}>
            {showDicomCdDownload && (
              <GroupDownloadButton
                groupKey={record.partNumber}
                groupRows={record.studies}
                orderId={orderId}
                isDownloadAllowed={isDownloadAllowed}
                showNotification={showNotification}
                classes={classes}
              />
            )}
          </Box>
        </Box>

        <EnhancedTable
          rows={record.studies}
          headCells={headCells}
          customCells={customCells}
          rowKey="id"
        />
      </DialogContent>
    </Dialog>
  );
};

export default withStyles(combinedStyles)(RecordDicomsDialog);
